// src/app/services/damperApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import { IDamper, IDamperParams, UpdatePayload } from '../models';

export const damperApi = createApi({
  reducerPath: 'damperApi',
  baseQuery,
  endpoints: (builder) => ({
    getDamperParams: builder.query<{ damper: IDamperParams }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getDamperHoodParams: builder.query<
      { damper: IDamperParams },
      { width: string; height: string }
    >({
      query: ({ width, height }) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/?max_height=${height}&max_width=${width}&hood=true`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
      extraOptions: {
        cacheData: true,
      },
    }),
    getDamper: builder.query<IDamper, number>({
      query: (id) => `/api/v1.0/ahu/dampers/${id}`,
      transformResponse: (data: IDamper) => {
        data.params = data.damper_params ? JSON.parse(data.damper_params) : {};
        data.mounting_planes = data.mounting_planes.filter(
          (el) => el.id < 7 || el.id === 9
        );
        return data;
      },
    }),
    getAllDampersCount: builder.query<{ count: number }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/dampers/all/count/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getDampersCount: builder.query<{ count: number }, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/dampers/all/count/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    getAllFansDampersCount: builder.query<{ count: number }, void>({
      query: () => `/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/all/count/`,
      keepUnusedDataFor: Infinity, // Keep the cached data until the page reloads
    }),
    getFansDampersCount: builder.query<{ count: number }, string>({
      query: (queryString) =>
        `/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/all/count/?${queryString}`,
      extraOptions: {
        cacheData: true,
      },
    }),
    updateDamper: builder.mutation<IDamper, UpdatePayload<IDamper>>({
      query: ({ id, body, signal }) => ({
        url: `/api/v1.0/ahu/dampers/${id}`,
        method: 'PATCH',
        body,
        signal,
      }),
      transformResponse: (data: IDamper) => {
        data.params = data.damper_params ? JSON.parse(data.damper_params) : {};
        data.mounting_planes = data.mounting_planes.filter(
          (el) => el.id < 7 || el.id === 9
        );
        return data;
      },
    }),
  }),
});

export const {
  useLazyGetDamperQuery,
  useGetDamperParamsQuery,
  useGetAllDampersCountQuery,
  useLazyGetDampersCountQuery,
  useGetAllFansDampersCountQuery,
  useLazyGetFansDampersCountQuery,
  useGetDamperQuery,
  useLazyGetDamperHoodParamsQuery,
  useUpdateDamperMutation,
} = damperApi;
